import { useState, useEffect, useRef } from 'react';
import type { FC, FormEvent, ChangeEvent } from 'react';
import { apiClient } from '../../utils/api-client';
import { LANGUAGES } from '../../i18n/languages';
import { Spinner } from '../../components/ui/spinner';
import { Dialog } from '../../components/ui/dialog';
import { useTranslation } from '../../i18n/I18nContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useBalance } from '../../contexts/BalanceContext';

const WORD_COUNT_OPTIONS = [250, 500, 1000];
const ARTICLE_STYLES = [
  { value: 'news article', key: 'article_style.news_article' },
  { value: 'blog post', key: 'article_style.blog_post' },
  { value: 'short story', key: 'article_style.short_story' },
  { value: 'dialogue', key: 'article_style.dialogue' },
  { value: 'essay', key: 'article_style.essay' },
  { value: 'letter', key: 'article_style.letter' },
];

type Tab = 'ai' | 'web';

/** File extension → contentType (matches polyreader-doc + reader-api accepted set). */
const SUPPORTED_FILE_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  md: 'text/markdown',
  markdown: 'text/markdown',
};

const FILE_INPUT_ACCEPT = '.pdf,.docx,.doc,.md,.markdown';

function getFileExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx === -1 ? '' : filename.slice(idx + 1).toLowerCase();
}

interface GenerateArticleProps {
  open: boolean;
  onClose: () => void;
  onGenerated: () => void;
}

const GenerateArticle: FC<GenerateArticleProps> = ({ open, onClose, onGenerated }) => {
  const { t } = useTranslation();
  const { settings, updateSettings } = useSettings();
  const [tab, setTab] = useState<Tab>('ai');
  const [language, setLanguage] = useState(settings.sourceLanguage);
  const [targetWordCount, setTargetWordCount] = useState(500);
  const [topic, setTopic] = useState('');
  const [style, setStyle] = useState('blog post');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [estimatedCost, setEstimatedCost] = useState<string | null>(null);
  const [estimatedCostCents, setEstimatedCostCents] = useState<number | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { balance, refreshBalance } = useBalance();

  const insufficientFunds = balance !== null && estimatedCostCents !== null && balance.effectiveBalance < estimatedCostCents;

  // Sync from settings when dialog opens
  useEffect(() => {
    if (!open) return;
    setLanguage(settings.sourceLanguage);
    setSubmitAttempted(false);
  }, [open, settings.sourceLanguage]);

  // Refresh balance when dialog opens
  useEffect(() => {
    if (open) refreshBalance();
  }, [open, refreshBalance]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const fetchEstimate = async () => {
      const estimateType = tab === 'web' ? 'url' : 'ai';
      try {
        const response = await apiClient.get<{ costFormatted: string; costCents: number }>(
          `/reader/articles/estimate?wordCount=${targetWordCount}&type=${estimateType}`
        );
        if (!cancelled) {
          setEstimatedCost(response.costFormatted);
          setEstimatedCostCents(response.costCents);
        }
      } catch {
        if (!cancelled) {
          setEstimatedCost(null);
          setEstimatedCostCents(null);
        }
      }
    };

    fetchEstimate();
    return () => { cancelled = true; };
  }, [targetWordCount, tab, open]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    setError('');
    if (!picked) {
      setFile(null);
      return;
    }
    const ext = getFileExtension(picked.name);
    if (!SUPPORTED_FILE_TYPES[ext]) {
      setFile(null);
      setError(t('generate.file_unsupported_type'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    // If a URL was previously typed, clear it — either/or input.
    if (url.trim()) setUrl('');
    setFile(picked);
  };

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setUrl(v);
    setError('');
    // If a file was previously picked, clear it — either/or input.
    if (v.trim() && file) {
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Upload the chosen file via presigned PUT. Returns the s3Key + contentType. */
  const uploadFileViaPresignedPut = async (picked: File): Promise<{ s3Key: string; contentType: string }> => {
    const ext = getFileExtension(picked.name);
    const contentType = SUPPORTED_FILE_TYPES[ext];
    const { uploadUrl, s3Key } = await apiClient.post<{ uploadUrl: string; s3Key: string }>(
      '/reader/articles/upload-url',
      { contentType, filename: picked.name },
    );

    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: picked,
    });
    if (!putResponse.ok) {
      throw new Error(`Upload failed (${putResponse.status})`);
    }
    return { s3Key, contentType };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitAttempted(true);

    if (tab === 'ai' && !topic.trim()) {
      setError(t('generate.topic_required'));
      return;
    }

    if (tab === 'web') {
      const hasUrl = url.trim() !== '';
      const hasFile = file !== null;

      if (!hasUrl && !hasFile) {
        setError(t('generate.url_or_file_required'));
        return;
      }
      if (hasUrl && hasFile) {
        // Should never happen given handleFileChange/handleUrlChange clearing,
        // but defend against it explicitly so the user gets a clear error.
        setError(t('generate.url_or_file_one'));
        return;
      }

      if (hasUrl) {
        const trimmed = url.trim();
        // Per spec: URLs must begin with http:// or https://.
        if (!/^https?:\/\//i.test(trimmed)) {
          setError(t('generate.url_invalid'));
          return;
        }
        try {
          const parsed = new URL(trimmed);
          if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
        } catch {
          setError(t('generate.url_invalid'));
          return;
        }
      } else {
        // hasFile — re-validate extension defensively.
        const ext = getFileExtension(file!.name);
        if (!SUPPORTED_FILE_TYPES[ext]) {
          setError(t('generate.file_unsupported_type'));
          return;
        }
      }
    }

    if (insufficientFunds) return;

    setIsSubmitting(true);

    try {
      if (tab === 'ai') {
        await apiClient.post('/reader/articles/generate', {
          source: 'ai',
          language,
          cefrLevel: settings.cefrLevel,
          targetWordCount,
          topic: topic.trim(),
          style,
        });
        if (language !== settings.sourceLanguage) {
          updateSettings({ sourceLanguage: language }).catch(() => {});
        }
      } else if (url.trim()) {
        await apiClient.post('/reader/articles/generate', {
          source: 'web',
          targetWordCount,
          url: url.trim(),
        });
      } else {
        // File upload path
        const upload = await uploadFileViaPresignedPut(file!);
        await apiClient.post('/reader/articles/generate', {
          source: 'web',
          targetWordCount,
          upload,
        });
      }

      setTopic('');
      setUrl('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setStyle('news article');
      onClose();
      onGenerated();
      window.dispatchEvent(new Event('balance-changed'));
    } catch (err) {
      console.error('Failed to create article:', err);
      const fallback = tab === 'web' ? t('generate.error_import') : t('generate.error');
      setError(err instanceof Error ? err.message : fallback);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled = isSubmitting
    || (tab === 'ai' && !topic.trim())
    || (tab === 'web' && !url.trim() && !file);

  const footerContent = (
    <div className="flex items-center justify-between">
      {estimatedCost ? (
        <span className="text-xs text-muted-foreground">
          {t('generate.estimated_cost', { cost: estimatedCost })}
        </span>
      ) : <span />}
      <button
        type="submit"
        form="generate-form"
        disabled={submitDisabled}
        className="bg-primary text-primary-foreground px-5 py-2 rounded-md font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <Spinner size="sm" className="border-primary-foreground" />
            {tab === 'web' ? t('generate.importing') : t('generate.generating')}
          </span>
        ) : (
          tab === 'web' ? t('generate.submit_web') : t('generate.submit_ai')
        )}
      </button>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} title={t('generate.title')} disableClose={isSubmitting} footer={footerContent}>
        {/* Tabs */}
        <div className="flex border-b border-border px-4 sm:px-6">
          {(['ai', 'web'] as const).map((t_) => (
            <button
              key={t_}
              type="button"
              onClick={() => { setTab(t_); setError(''); setSubmitAttempted(false); }}
              disabled={isSubmitting}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                tab === t_
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t_ === 'ai' ? t('generate.tab_ai') : t('generate.tab_web')}
              {tab === t_ && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          ))}
        </div>

        <form id="generate-form" onSubmit={handleSubmit} className="px-4 sm:px-6 py-4 space-y-4">
          {error && (
            <div className="bg-destructive text-destructive-foreground rounded-md p-3 text-sm">
              {error}
            </div>
          )}

          {submitAttempted && insufficientFunds && !error && (
            <div className="bg-warning text-warning-foreground rounded-md p-3 text-sm">
              {t('generate.insufficient_funds')}
            </div>
          )}

          {tab === 'ai' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">{t('generate.language')}</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
                >
                  {LANGUAGES.filter((lang) => lang.code !== settings.language).map((lang) => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {tab === 'web' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">{t('generate.url')}</label>
                <input
                  type="url"
                  value={url}
                  onChange={handleUrlChange}
                  disabled={isSubmitting || file !== null}
                  placeholder={t('generate.url_placeholder')}
                  className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm disabled:opacity-50"
                />
              </div>

              <div className="text-center text-xs text-muted-foreground">
                {t('generate.or')}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('generate.file')}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FILE_INPUT_ACCEPT}
                  onChange={handleFileChange}
                  disabled={isSubmitting || url.trim() !== ''}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting || url.trim() !== ''}
                    className="py-1 px-3 rounded-md bg-muted text-foreground text-sm disabled:opacity-50"
                  >
                    {t('generate.file_choose')}
                  </button>
                  <span className="text-sm text-muted-foreground truncate">
                    {file ? file.name : t('generate.file_none')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('generate.file_help')}</p>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">{t('generate.word_count')}</label>
            <select
              value={targetWordCount}
              onChange={(e) => setTargetWordCount(parseInt(e.target.value))}
              disabled={isSubmitting}
              className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
            >
              {WORD_COUNT_OPTIONS.map((count) => (
                <option key={count} value={count}>{t('generate.words_approx', { count })}</option>
              ))}
            </select>
          </div>

          {tab === 'ai' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">{t('generate.topic')}</label>
                <textarea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t('generate.topic_placeholder')}
                  rows={3}
                  className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('generate.style')}</label>
                <select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground text-sm"
                >
                  {ARTICLE_STYLES.map((s) => (
                    <option key={s.value} value={s.value}>{t(s.key)}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </form>
    </Dialog>
  );
};

export default GenerateArticle;
